import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FacebookIcon from '@mui/icons-material/Facebook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import IOSSwitch from 'src/components/switch/switch.component';

export default function FacebookAccounts({ accounts = [], isLoading, onConnect, onReauthenticate, onRefresh, refreshingAccount, onDelete, onToggleEnable }) {
  if (!isLoading && !accounts.length) {
    return (
      <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <FacebookIcon sx={{ color: '#1877F2', fontSize: 42, mb: 1 }} />
        <Typography fontWeight={800}>No integrated Facebook pages</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Add a Facebook page to manage Messenger conversations.
        </Typography>
        <Button variant="contained" onClick={onConnect}>
          Add Facebook Page
        </Button>
      </Box>
    );
  }

  if (!accounts.length) return null;

  return (
    <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Page</TableCell>
            <TableCell>Page ID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => {
            const isActive = account.is_active !== false;
            const isEnabled = account.is_enable !== false;
            return (
              <TableRow key={account.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar src={account.picture_url || ''} sx={{ bgcolor: alpha('#1877F2', 0.14), color: '#1877F2' }}>
                      <FacebookIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={800} noWrap>
                        {account.display_name || 'Facebook Page'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {account.category || 'Page'}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{account.profile_id}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                    <Chip size="small" color={isActive ? 'success' : 'error'} label={isActive ? 'Active' : 'Inactive'} />
                    <Chip size="small" color={isEnabled ? 'primary' : 'default'} label={isEnabled ? 'Enabled' : 'Disabled'} />
                    {Number(account.unread_count || 0) > 0 && <Chip size="small" color="error" label={`${account.unread_count} new`} />}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {account.profile_link && (
                      <Tooltip title="Open Facebook page">
                        <IconButton
                          component="a"
                          href={account.profile_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          aria-label="Open Facebook page"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={isActive ? 'Enable or disable this page' : 'Re-authenticate this page to activate it'}>
                      <span>
                        <IOSSwitch
                          checked={isEnabled && isActive}
                          disabled={!isActive}
                          onChange={(event) => onToggleEnable('facebook', account, event.target.checked)}
                        />
                      </span>
                    </Tooltip>
                    <Button size="small" component={RouterLink} disabled={!isActive || !isEnabled} to={`/facebook-chat?page_id=${encodeURIComponent(account.profile_id)}`}>
                      Open Messages
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => onReauthenticate(account)}>
                      Re-authenticate
                    </Button>
                    <Tooltip title="Refresh page name and picture">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Refresh Facebook page details"
                          disabled={refreshingAccount === `facebook:${account.id}`}
                          onClick={() => onRefresh('facebook', account)}
                        >
                          <SyncIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button size="small" color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete('facebook', account)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
